import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Shield, Globe, Plus, Search, Trash2, Loader2, ShieldCheck, ShieldAlert, AlertCircle, X, AlertTriangle, Bell, Info } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PortalTopNav } from '@/components/portal/PortalTopNav';
import { Label } from '@/components/ui/label';
import { Helmet } from '@/components/Helmet';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getDomainLabel, TIER_VARIANT_BY_PRICE_ID } from '@/components/portal/pricing';
import { CANCELLATION_FEES, type TierType } from '@/components/portal/TierSelection';
import { CurrencyDock } from '@/components/portal/CurrencyDock';
import { openExternalUrl } from '@/lib/navigation';
import { useCurrency } from '@/hooks/use-currency';
import { resolveEffectiveSubscription } from '@/lib/subscription-resolver';
import { normalizeOwaspStatus } from '@/lib/owasp';
import { STATUS_LABELS } from '@/lib/vocabulary/securityTerminology';
import { usePortfolioPricingApplication } from '@/hooks/use-portfolio-pricing-application';
import { useReviewedPricingSchedule } from '@/hooks/use-reviewed-pricing-schedule';
import { PortfolioPricingStatusCard } from '@/components/portal/PortfolioPricingStatusCard';

// Map price_id to tier name for display
const TIER_BY_PRICE_ID: Record<string, { name: string; variant: 'default' | 'secondary' }> = Object.entries(TIER_VARIANT_BY_PRICE_ID).reduce(
  (acc, [priceId, variant]) => {
    acc[priceId] = { name: getDomainLabel({ stripe_price_id: priceId }), variant };
    return acc;
  },
  {} as Record<string, { name: string; variant: 'default' | 'secondary' }>,
);

// Maintenance mode flag - set to true to enable maintenance mode
const MAINTENANCE_MODE = false;
const SECURITY_NOTICE_DISMISS_KEY = 'dashboard-security-notice-dismissed';
const SETUP_NOTICE_DISMISS_KEY = 'dashboard-setup-notice-dismissed';
const HEARTBEAT_REPAIR_THRESHOLD_MS = 60 * 60 * 1000;
const HEARTBEAT_TTL_MS = 24 * 60 * 60 * 1000;

const normalizeSiteUrl = (value: string) => {
  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    return url.hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return value.replace(/^https?:\/\//, '').replace(/^www\./, '').toLowerCase();
  }
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown';
  return new Date(value).toLocaleString();
};

const isHeartbeatFresh = (value?: string | null) => {
  if (!value) return false;
  const last = new Date(value).getTime();
  if (Number.isNaN(last)) return false;
  return Date.now() - last <= HEARTBEAT_TTL_MS;
};

type ConnectionState = 'connected' | 'repair_needed' | 'disconnected';

const getConnectionState = (value?: string | null): ConnectionState => {
  if (!value) return 'disconnected';
  const last = new Date(value).getTime();
  if (Number.isNaN(last)) return 'disconnected';
  const age = Date.now() - last;
  if (age <= HEARTBEAT_REPAIR_THRESHOLD_MS) return 'connected';
  if (age <= HEARTBEAT_TTL_MS) return 'repair_needed';
  return 'disconnected';
};

const isHeartbeatRepairNeeded = (value?: string | null) => getConnectionState(value) === 'repair_needed';

type DashboardProps = {
  allowAdminView?: boolean;
  impersonationExitTo?: string;
  impersonationContext?: {
    customer?: {
      id: string;
      email?: string | null;
      fullName?: string | null;
      companyName?: string | null;
    };
    profile?: {
      full_name?: string | null;
      payment_lock_bypass?: boolean | null;
      is_test_user?: boolean | null;
      subscription_emulation_status?: string | null;
      subscription_emulation_grace_end?: string | null;
      subscription_emulation_suspended_since?: string | null;
    };
    domains?: any[];
    managedSites?: any[];
    alerts?: any[];
    loginContainmentAlerts?: any[];
    notifications?: any[];
  } | null;
};

export default function Dashboard({ allowAdminView = false, impersonationExitTo, impersonationContext }: DashboardProps) {
  const { user, signOut, loading } = useAuth();
  const { clearImpersonation } = useImpersonation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { checking } = useRequireMfa();
  const isImpersonationView = Boolean(impersonationContext?.customer?.id);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingDomainId, setDeletingDomainId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'active' | 'alerts' | 'disconnected'>('all');
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedAlertDomain, setSelectedAlertDomain] = useState<any | null>(null);
  const [expandedDomains, setExpandedDomains] = useState<Record<string, boolean>>({});
  const [resumingDomainId, setResumingDomainId] = useState<string | null>(null);
  const [showSecurityNotice, setShowSecurityNotice] = useState(true);
  const [showSetupNotice, setShowSetupNotice] = useState(true);
  const [confirmCancelFees, setConfirmCancelFees] = useState<Record<string, boolean>>({});
  const [impersonationForceRemove, setImpersonationForceRemove] = useState<Record<string, boolean>>({});
  const [impersonationForceReason, setImpersonationForceReason] = useState<Record<string, string>>({});
  const { formatCurrency } = useCurrency();
  const loginContainmentWindow = '30d' as const;
  const { data: isAdmin } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user && !checking,
  });
  const allowCustomerView = allowAdminView || isAdmin !== true || isImpersonationView;
  const queriesEnabled = !!user && !checking && !MAINTENANCE_MODE && allowCustomerView && !isImpersonationView;

  useEffect(() => {
    if (allowAdminView) return;
    if (isAdmin && !loading && !checking) {
      navigate('/portal/admin', { replace: true });
    }
  }, [allowAdminView, checking, isAdmin, loading, navigate]);


  const isPendingStatus = (status: string) =>
    status === 'pending' || status === 'payment_needed' || status === 'payment_incomplete' || status === 'pending_payment';
  const isPaymentNeeded = (status: string) =>
    status === 'payment_needed' || status === 'payment_incomplete' || status === 'pending_payment';
  const getTierType = (domain: { stripe_price_id?: string | null; stripePriceId?: string | null; promo_price?: number | null; promoPrice?: number | null; tier?: string | null }): TierType => {
    if (domain.tier === 'basic' || domain.tier === 'essential' || domain.tier === 'premium') {
      return domain.tier;
    }
    const label = getDomainLabel(domain).toLowerCase();
    if (label.includes('basic')) return 'basic';
    if (label.includes('essential')) return 'essential';
    return 'premium';
  };
  type StatusTone = 'success' | 'warning' | 'danger' | 'neutral' | 'info';
  const getStatusBadgeVariant = (tone: StatusTone) => {
    switch (tone) {
      case 'success':
      case 'warning':
      case 'danger':
      case 'info':
        return tone;
      default:
        return 'neutral';
    }
  };
  const getSecurityHealthLabel = (status: 'healthy' | 'attention' | 'high_risk' | 'monitoring_planned') => {
    switch (status) {
      case 'high_risk':
        return { label: STATUS_LABELS.actionRequired, tone: 'danger' as StatusTone };
      case 'attention':
        return { label: STATUS_LABELS.needsHost, tone: 'warning' as StatusTone };
      case 'healthy':
        return { label: STATUS_LABELS.covered, tone: 'success' as StatusTone };
      default:
        return { label: STATUS_LABELS.monitoringPlanned, tone: 'neutral' as StatusTone };
    }
  };
  const getBillingState = (status: string) => {
    const effective = resolveEffectiveSubscription({
      domainStatus: status,
      isTestUser,
      emulationStatus: emulatedStatus,
      graceEnd: profile?.subscription_emulation_grace_end || null,
      suspendedSince: profile?.subscription_emulation_suspended_since || null,
    });
    if (effective.status === 'active') return { label: 'Active', tone: 'success' as StatusTone };
    if (effective.status === 'past_due') return { label: 'Past due', tone: 'warning' as StatusTone };
    if (effective.status === 'trial') return { label: 'Trial active', tone: 'info' as StatusTone };
    if (effective.status === 'cancelled' || effective.status === 'suspended') return { label: 'Suspended', tone: 'danger' as StatusTone };
    return { label: 'Billing paused', tone: 'neutral' as StatusTone };
  };
  const getProtectionState = (status: string) => {
    const effective = resolveEffectiveSubscription({
      domainStatus: status,
      isTestUser,
      emulationStatus: emulatedStatus,
      graceEnd: profile?.subscription_emulation_grace_end || null,
      suspendedSince: profile?.subscription_emulation_suspended_since || null,
    });
    if (effective.status === 'active') return { label: 'Live', tone: 'success' as StatusTone, rank: 3 };
    if (effective.status === 'past_due') return { label: 'Live (no updates)', tone: 'warning' as StatusTone, rank: 2 };
    if (effective.status === 'trial') return { label: 'Live during trial', tone: 'info' as StatusTone, rank: 3 };
    if (effective.status === 'cancelled' || effective.status === 'suspended') return { label: 'Paused', tone: 'danger' as StatusTone, rank: 0 };
    return { label: 'Live', tone: 'neutral' as StatusTone, rank: 1 };
  };
  const protectionTooltipCopy: Record<string, { title: string; body: string }> = {
    Live: {
      title: 'Protection: Live',
      body: 'Protection updates and monitoring are active.',
    },
    'Live (no updates)': {
      title: 'Protection: Live (no updates)',
      body: 'Protection continues at the last applied level. New updates and configuration changes are paused until billing resumes.',
    },
    'Live during trial': {
      title: 'Protection: Live during trial',
      body: 'Your 7-day free trial is active for this domain. The trial is capped at up to 3 eligible domains per account, protection remains live, and billing begins only after the trial ends.',
    },
    Outdated: {
      title: 'Protection: Outdated',
      body: 'This site is not receiving updates and protection may be outdated. Reactivate to restore live updates.',
    },
    Paused: {
      title: 'Protection: Paused',
      body: 'Protection is paused due to subscription status. Reactivate to restore protection.',
    },
  };

  // Check if user has payment method
  const { data: paymentStatusData } = useQuery({
    queryKey: ['payment-method', user?.id],
    queryFn: async () => {
      // Ensure we have a valid session before calling the edge function
      const { data: { session } } = await supabase.auth.getSession();
      let activeSession = session;
      if (activeSession?.expires_at && activeSession.expires_at * 1000 - Date.now() <= 60_000) {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data.session) {
          activeSession = data.session;
        }
      }
      if (!activeSession) {
        console.warn('Payment method check skipped: no active session');
        toast.error('Your session expired. Please log in again.');
        navigate('/portal/auth');
        return { hasPaymentMethod: false };
      }
      
      const { data, error } = await supabase.functions.invoke('check-payment-method', {
        headers: {
          Authorization: `Bearer ${activeSession.access_token}`,
        },
      });
      if (error) {
        // If auth error, return no payment method rather than throwing
        console.warn('Payment method check failed:', error.message);
        return { hasPaymentMethod: false };
      }
      return data;
    },
    enabled: queriesEnabled,
    retry: false, // Don't retry on auth errors
    staleTime: 30000, // Cache for 30 seconds to avoid repeated calls
  });
  const paymentStatus = isImpersonationView ? null : paymentStatusData;

  const { data: profileData } = useQuery({
    queryKey: ['profile-payment-bypass', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('payment_lock_bypass, is_test_user, subscription_emulation_status, subscription_emulation_grace_end, subscription_emulation_suspended_since, full_name')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: queriesEnabled,
  });

  const profile = isImpersonationView ? impersonationContext?.profile ?? null : profileData;
  const { data: portfolioPricingApplication } = usePortfolioPricingApplication(
    isImpersonationView ? null : user?.id,
    queriesEnabled,
  );
  const { data: latestReviewedPricingSchedule } = useReviewedPricingSchedule(
    isImpersonationView ? null : user?.id,
    queriesEnabled,
  );
  const paymentBypassUser = Boolean(profile?.payment_lock_bypass);
  const isTestUser = Boolean(profile?.is_test_user);
  const emulatedStatus = profile?.subscription_emulation_status || null;
  const customerDisplayName = isImpersonationView
    ? (impersonationContext?.customer?.fullName
        || impersonationContext?.customer?.companyName
        || impersonationContext?.customer?.email
        || 'Unknown customer')
    : (profile?.full_name
        || user?.user_metadata?.full_name
        || user?.email
        || 'Account');
  const accountMenuName = isImpersonationView
    ? (user?.user_metadata?.full_name || user?.email || 'Account')
    : customerDisplayName;

  const { data: domainsData, isLoading: domainsLoading } = useQuery({
    queryKey: ['domains', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('user_id', user!.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: queriesEnabled,
  });
  const domains = isImpersonationView ? impersonationContext?.domains ?? [] : domainsData;

  const { data: managedSitesData } = useQuery({
    queryKey: ['managed-sites', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('managed_sites' as any)
        .select('id, domain_id, site_url, wp_home, wp_admin_url, site_token_hash, last_heartbeat_at, plugin_version, wp_version, php_version, can_install_plugins') as any)
        .eq('user_id', user!.id);
      if (error) throw error;
      return data || [];
    },
    enabled: queriesEnabled,
  });
  const managedSites = isImpersonationView ? impersonationContext?.managedSites ?? [] : managedSitesData;

  // Fetch security alerts
  const domainIds = domains?.map(d => d.id) || [];

  const { data: alertsData } = useQuery({
    queryKey: ['security_alerts', user?.id, domainIds.join(',')],
    queryFn: async () => {
      if (domainIds.length === 0) return [];

      const query = supabase
        .from('security_alerts')
        .select('*')
        .eq('resolved', false)
        .order('created_at', { ascending: false })
        .in('domain_id', domainIds);

      const { data, error } = await query;
      
      if (error) throw error;
      return data;
    },
    enabled: queriesEnabled && domainsData !== undefined,
  });
  const alerts = isImpersonationView ? impersonationContext?.alerts ?? [] : alertsData;

  const { data: loginContainmentAlertsData } = useQuery({
    queryKey: ['login-containment-alerts', user?.id, domainIds.join(','), loginContainmentWindow],
    queryFn: async () => {
      if (domainIds.length === 0) return [];
      const since = loginContainmentWindow === '30d'
        ? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
        : null;
      let query = supabase
        .from('security_alerts')
        .select('id, domain_id, alert_type, created_at')
        .in('domain_id', domainIds)
        .in('alert_type', ['AUTH_USER_LOCKED', 'AUTH_IP_TEMP_BLOCKED']);
      if (since) {
        query = query.gte('created_at', since);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: queriesEnabled && domainsData !== undefined,
  });
  const loginContainmentAlerts = isImpersonationView
    ? impersonationContext?.loginContainmentAlerts ?? []
    : loginContainmentAlertsData;

  const { data: domainLogEntries } = useQuery({
    queryKey: ['domain-logs-health', user?.id, domainIds.join(',')],
    queryFn: async () => {
      if (domainIds.length === 0) return [];
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('domain_logs')
        .select('domain_id, owasp_id, status, last_checked_at, created_at')
        .in('domain_id', domainIds)
        .gte('created_at', cutoff);
      if (error) throw error;
      return data || [];
    },
    enabled: queriesEnabled && domainsData !== undefined && domainIds.length > 0,
  });

  const { data: postureRows } = useQuery({
    queryKey: ['domain-security-posture', user?.id, domainIds.join(',')],
    queryFn: async () => {
      if (domainIds.length === 0) return [];
      const { data, error } = await supabase
        .from('domain_security_posture')
        .select('domain_id, coverage_score, posture_score, computed_at')
        .in('domain_id', domainIds);
      if (error) throw error;
      return data || [];
    },
    enabled: queriesEnabled && domainsData !== undefined && domainIds.length > 0,
  });

  const { data: notificationsData = [] } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('notifications')
        .select('id, title, body, severity, created_at, read_at, dismissed_at')
        .eq('user_id', user.id)
        .is('dismissed_at', null)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data || [];
    },
    enabled: queriesEnabled,
  });
  const notifications = isImpersonationView ? impersonationContext?.notifications ?? [] : notificationsData;

  const unreadNotifications = useMemo(
    () => notifications.filter((item: any) => !item.read_at),
    [notifications],
  );
  const bannerNotifications = useMemo(
    () => unreadNotifications.filter((item: any) => !item.dismissed_at),
    [unreadNotifications],
  );
  const dismissNotification = async (notificationId: string) => {
    const notificationOwnerId = isImpersonationView ? impersonationContext?.customer?.id : user?.id;
    if (!notificationOwnerId || !notificationId) return;
    const { error } = await supabase
      .from('notifications')
      .update({ dismissed_at: new Date().toISOString() })
      .eq('id', notificationId)
      .eq('user_id', notificationOwnerId);
    if (error) {
      toast.error('Unable to dismiss notification.');
      return;
    }
    if (isImpersonationView && impersonationContext?.customer?.id) {
      queryClient.invalidateQueries({
        queryKey: ['admin-impersonation-context', impersonationContext.customer.id],
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    }
  };

  // Get domain IDs with active alerts
  const domainIdsWithAlerts = new Set(alerts?.map(a => a.domain_id) || []);
  const actionableIssueCount = alerts?.length || 0;
  const loginContainmentCount = loginContainmentAlerts?.length || 0;
  const loginContainmentByDomain = useMemo(() => {
    const map = new Map<string, number>();
    (loginContainmentAlerts || []).forEach((alert: any) => {
      map.set(alert.domain_id, (map.get(alert.domain_id) || 0) + 1);
    });
    return map;
  }, [loginContainmentAlerts]);
  const issuesByDomain = useMemo(() => {
    const map = new Map<string, number>();
    (alerts || []).forEach((alert: any) => {
      map.set(alert.domain_id, (map.get(alert.domain_id) || 0) + 1);
    });
    return map;
  }, [alerts]);
  const domainHealthSummary = useMemo(() => {
    const healthByDomainId = new Map<string, { status: 'healthy' | 'attention' | 'high_risk' | 'monitoring_planned'; lastChecked?: string | null }>();
    const worstStatusByDomain = new Map<string, string>();
    const lastCheckedByDomain = new Map<string, string>();
    const categoriesByDomain = new Map<string, Set<string>>();

    (domainLogEntries || []).forEach((entry: any) => {
      if (!entry.domain_id || !entry.owasp_id) return;
      const normalized = normalizeOwaspStatus(entry.status);
      const status = normalized === 'resolved' ? 'ok' : normalized;

      const current = worstStatusByDomain.get(entry.domain_id);
      if (!current || status === 'critical' || (status === 'warn' && current !== 'critical')) {
        worstStatusByDomain.set(entry.domain_id, status);
      }

      const categorySet = categoriesByDomain.get(entry.domain_id) || new Set<string>();
      categorySet.add(entry.owasp_id);
      categoriesByDomain.set(entry.domain_id, categorySet);

      const candidate = entry.last_checked_at || entry.created_at;
      if (candidate && (!lastCheckedByDomain.get(entry.domain_id) || candidate > lastCheckedByDomain.get(entry.domain_id)!)) {
        lastCheckedByDomain.set(entry.domain_id, candidate);
      }
    });

    (domains || []).forEach((domain: any) => {
      const worst = worstStatusByDomain.get(domain.id);
      const categoryCount = categoriesByDomain.get(domain.id)?.size || 0;
      let status: 'healthy' | 'attention' | 'high_risk' | 'monitoring_planned' = 'monitoring_planned';
      if (worst === 'critical') status = 'high_risk';
      else if (worst === 'warn') status = 'attention';
      else if (worst) status = 'healthy';
      else if (categoryCount > 0) status = 'healthy';

      if (categoryCount === 0) status = 'monitoring_planned';
      healthByDomainId.set(domain.id, { status, lastChecked: lastCheckedByDomain.get(domain.id) });
    });

    return { healthByDomainId, lastCheckedByDomain };
  }, [domainLogEntries, domains]);

  const postureByDomain = useMemo(() => {
    const map = new Map<string, { coverage_score: number; posture_score: number; computed_at?: string }>();
    (postureRows || []).forEach((row: any) => {
      if (!row.domain_id) return;
      map.set(row.domain_id, {
        coverage_score: row.coverage_score ?? 0,
        posture_score: row.posture_score ?? 0,
        computed_at: row.computed_at ?? undefined,
      });
    });
    return map;
  }, [postureRows]);
  const selectedDomainAlerts = selectedAlertDomain
    ? (alerts?.filter(a => a.domain_id === selectedAlertDomain.id) || [])
    : (alerts || []);

  const managedSiteByDomainId = useMemo(() => {
    const map = new Map<string, any>();
    (managedSites || []).forEach((site: any) => {
      if (site.domain_id) {
        map.set(site.domain_id, site);
      }
    });
    if (domains) {
      domains.forEach((domain: any) => {
        if (map.has(domain.id)) return;
        const match = (managedSites || []).find((site: any) => {
          if (!site.site_url) return false;
          // Skip sites already linked to a different domain
          if (site.domain_id && site.domain_id !== domain.id) return false;
          return normalizeSiteUrl(site.site_url) === normalizeSiteUrl(domain.domain_name);
        });
        if (match) map.set(domain.id, match);
      });
    }
    return map;
  }, [managedSites, domains]);
  const accountProtectionState = useMemo(() => {
    const relevantDomains = (domains || []).filter((domain: any) => domain.status !== 'deleted');
    if (relevantDomains.length === 0) {
      return { label: 'Not available', tone: 'muted' as const };
    }
    const worstState = relevantDomains.reduce(
      (current, domain: any) => {
        const state = getProtectionState(domain.status);
        return state.rank < current.rank ? state : current;
      },
      getProtectionState(relevantDomains[0].status),
    );
    return { label: worstState.label, tone: worstState.tone };
  }, [domains]);
  const accountLastCheckIn = useMemo(() => {
    const timestamps = (managedSites || [])
      .map((site: any) => site.last_heartbeat_at)
      .filter(Boolean) as string[];
    if (timestamps.length === 0) return 'Not yet';
    const latest = timestamps.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
    return formatTimestamp(latest);
  }, [managedSites]);
  const accountBillingState = useMemo(() => {
    if (!domains || domains.length === 0) return null;
    const resolved = resolveEffectiveSubscription({
      domainStatus: domains[0]?.status,
      isTestUser,
      emulationStatus: emulatedStatus,
      graceEnd: profile?.subscription_emulation_grace_end || null,
      suspendedSince: profile?.subscription_emulation_suspended_since || null,
    });
    if (resolved.source === 'test_override') {
      if (resolved.status === 'active') return 'active';
      if (resolved.status === 'past_due') return 'past_due';
      if (resolved.status === 'suspended') return 'suspended';
      if (resolved.status === 'cancelled') return 'paused';
      if (resolved.status === 'trial') return 'active';
    }
    if (domains.some((domain: any) => isPaymentNeeded(domain.status))) return 'past_due';
    if (domains.some((domain: any) => domain.status === 'suspended')) return 'suspended';
    if (domains.some((domain: any) => domain.status === 'cancelled')) return 'paused';
    return 'active';
  }, [domains, isTestUser, emulatedStatus, profile?.subscription_emulation_grace_end, profile?.subscription_emulation_suspended_since]);

  // Delete confirmation input
  const [confirmDomainInput, setConfirmDomainInput] = useState('');

  const handleManageBilling = async () => {
    try {
      const targetUserId = isImpersonationView ? impersonationContext?.customer?.id : null;
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: targetUserId ? { targetUserId } : {},
      });
      if (error) throw error;
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      if (data?.url) {
        openExternalUrl(data.url);
        if (data.type === 'setup') {
          toast.info('Please add a payment method to continue.');
        }
      }
    } catch (err) {
      toast.error('Unable to open billing portal. Please add a domain first.');
    }
  };


  const handleAddDomain = () => {
    // Payment method is set up during add domain flow via Stripe checkout
    navigate('/portal/add-domain');
  };

  const handleRepairConnection = (domainId: string) => {
    navigate(`/portal/add-domain?step=secure&domain_id=${domainId}`);
  };

  const toggleDomainDetails = (domainId: string) => {
    setExpandedDomains((prev) => ({
      ...prev,
      [domainId]: !prev[domainId],
    }));
  };

  const enqueueSiteCommand = async (siteId: string, type: string, payload: Record<string, unknown> = {}) => {
    const { error } = await (supabase
      .from('site_commands' as any) as any)
      .insert({ site_id: siteId, type, payload });
    if (error) throw error;
  };

  const handleSelfCheck = async (managedSite?: any) => {
    if (!managedSite?.id) {
      toast.error('Connect the site before refreshing status.');
      return;
    }
    try {
      await enqueueSiteCommand(managedSite.id, 'run_inventory');
      toast.success('Status refresh queued. Updates will appear shortly.');
    } catch (err: any) {
      console.error('status refresh failed:', err);
      toast.error(err?.message || 'Unable to queue status refresh.');
    }
  };

  useEffect(() => {
    const dismissed = sessionStorage.getItem(SECURITY_NOTICE_DISMISS_KEY) === '1';
    setShowSecurityNotice(!dismissed);
    const setupDismissed = sessionStorage.getItem(SETUP_NOTICE_DISMISS_KEY) === '1';
    setShowSetupNotice(!setupDismissed);
  }, []);

  const handleDismissSecurityNotice = () => {
    setShowSecurityNotice(false);
    sessionStorage.setItem(SECURITY_NOTICE_DISMISS_KEY, '1');
  };

  const handleDismissSetupNotice = () => {
    setShowSetupNotice(false);
    sessionStorage.setItem(SETUP_NOTICE_DISMISS_KEY, '1');
  };

  const handleResumePayment = async (domain: any) => {
    if (!domain?.id) return;
    setResumingDomainId(domain.id);
    try {
      if (isImpersonationView) {
        const requestId = domain?.pending_purchase_request?.id;
        if (!requestId) {
          throw new Error('No pending purchase request was found for this domain.');
        }
        const { data, error } = await supabase.functions.invoke('admin-resend-domain-purchase-request', {
          body: { requestId },
        });
        if (error) throw error;
        if (data?.success) {
          toast.success('Customer payment email resent.');
          if (impersonationContext?.customer?.id) {
            queryClient.invalidateQueries({
              queryKey: ['admin-impersonation-context', impersonationContext.customer.id],
            });
          }
        } else {
          toast.error('Unable to resend customer payment email.');
        }
      } else {
        const { data, error } = await supabase.functions.invoke('resume-checkout', {
          body: { domainId: domain.id },
        });
        if (error) throw error;
        if (data?.url) {
          window.location.assign(data.url);
          toast.success('Redirecting to checkout to complete payment.');
        } else {
          toast.error('Unable to resume payment. Please try again.');
        }
      }
    } catch (err: any) {
      console.error('Resume payment failed:', err);
      toast.error(err?.message || (isImpersonationView ? 'Unable to resend customer payment email.' : 'Unable to resume payment.'));
    } finally {
      setResumingDomainId(null);
    }
  };

  const handleDeleteDomain = async (domain: any) => {
    const paymentIssue = isPaymentNeeded(domain.status);

    if (confirmDomainInput.trim().toLowerCase() !== domain.domain_name.trim().toLowerCase()) {
      toast.error('Please type the domain name exactly to confirm removal.');
      return;
    }

    if (isImpersonationView) {
      const forceRemove = Boolean(impersonationForceRemove[domain.id]);
      const forceReason = (impersonationForceReason[domain.id] || '').trim();
      if (forceRemove && !forceReason) {
        toast.error('Provide a reason before force removing this domain.');
        return;
      }

      setDeletingDomainId(domain.id);
      try {
        const { data, error } = await supabase.functions.invoke('admin-domain-removal-request', {
          body: {
            action: forceRemove ? 'force-remove' : 'request',
            domainId: domain.id,
            reason: forceRemove ? forceReason : undefined,
          },
        });
        if (error || data?.error) {
          throw error || new Error(data?.error || 'Unable to process domain removal.');
        }

        if (forceRemove) {
          toast.success('Domain force removed. Customer and support were notified.');
        } else {
          toast.success('Confirmation email sent to the customer. No cancellation fee was charged.');
        }
        if (impersonationContext?.customer?.id) {
          queryClient.invalidateQueries({
            queryKey: ['admin-impersonation-context', impersonationContext.customer.id],
          });
        }
        queryClient.invalidateQueries({ queryKey: ['domains'] });
        queryClient.invalidateQueries({ queryKey: ['invoices', user?.id] });
      } catch (err: any) {
        console.error('Impersonation domain removal error:', err);
        toast.error(err?.message || 'Failed to process domain removal request');
      } finally {
        setDeletingDomainId(null);
        setConfirmDomainInput('');
        setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: false }));
        setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
      }
      return;
    }

    // Check if user has a payment method before allowing delete
    if (!paymentStatus?.hasPaymentMethod && !paymentIssue) {
      toast.error('You need a payment method on file to delete domains. Please add a domain first to set up billing.');
      return;
    }

    setDeletingDomainId(domain.id);
    try {
      let chargeResult: { charged?: boolean; reason?: string; error?: string } | null = null;
      // Charge the cancellation fee first unless the domain is already in a payment-needed state
      if (!paymentIssue) {
        const { data, error: chargeError } = await supabase.functions.invoke('charge-cancellation-fee', {
          body: { domainName: domain.domain_name, subscriptionId: domain.stripe_subscription_id },
        });
        chargeResult = data || null;

        if (chargeError) {
          console.error('Cancellation fee charge error:', chargeError);
          toast.error('Failed to process cancellation fee. Please try again.');
          setDeletingDomainId(null);
          return;
        }

        if (chargeResult?.error) {
          console.error('Cancellation fee error:', chargeResult.error);
          toast.error('Failed to process cancellation fee: ' + chargeResult.error);
          setDeletingDomainId(null);
          return;
        }

        if (!chargeResult?.charged) {
          if (chargeResult?.reason === 'free_trial') {
            toast.info('This domain is still in the 7-day free trial. No cancellation fee will be charged.');
          } else if (chargeResult?.reason === 'no_fee') {
            toast.info('This reviewed pricing domain does not have a cancellation fee.');
          } else {
            toast.error('Unable to charge cancellation fee. Please update your payment method.');
            setDeletingDomainId(null);
            return;
          }
        }
      }

      // Soft-delete the domain (set deleted_at, status becomes cancelled)
      const { error } = await supabase
        .from('domains')
        .update({
          status: 'cancelled',
          deleted_at: new Date().toISOString(),
        })
        .eq('id', domain.id);

      if (error) throw error;

      // Send notification email with full details
      await supabase.functions.invoke('notify-domain-change', {
        body: { 
          action: 'deleted',
          domains: [{
            domainName: domain.domain_name,
            wpAdminUrl: domain.wp_admin_url,
            status: domain.status,
            createdAt: domain.created_at,
          }],
          userEmail: user!.email,
          changes: paymentIssue
            ? `Domain "${domain.domain_name}" was deleted by the customer while payment was incomplete. No cancellation fee was charged.`
            : chargeResult?.reason === 'free_trial'
              ? `Domain "${domain.domain_name}" was deleted by the customer during the 7-day free trial. No cancellation fee was charged.`
              : chargeResult?.reason === 'no_fee'
                ? `Domain "${domain.domain_name}" was deleted under reviewed pricing terms. No cancellation fee was charged.`
                : `Domain "${domain.domain_name}" was deleted by the customer. Cancellation fee was charged.`,
        },
      });

      // Refresh the domains list and invoices
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', user?.id] });
      toast.success(
        paymentIssue
          ? 'Domain deleted. No cancellation fee due to incomplete payment.'
          : chargeResult?.reason === 'free_trial'
            ? 'Domain deleted during the 7-day free trial. No cancellation fee was charged.'
            : chargeResult?.reason === 'no_fee'
              ? 'Domain deleted. No cancellation fee applies under the reviewed pricing terms.'
              : 'Domain deleted. Cancellation fee charged.'
      );
    } catch (err: any) {
      console.error('Delete domain error:', err);
      toast.error('Failed to delete domain');
    } finally {
      setDeletingDomainId(null);
      setConfirmDomainInput('');
    }
  };

  // Show maintenance page
  if (MAINTENANCE_MODE) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <Card className="cyber-card max-w-md mx-4 relative z-10">
          <CardContent className="p-8 text-center">
            <Shield className="h-16 w-16 mx-auto text-primary mb-6 animate-pulse" />
            <h1 className="text-2xl font-mono font-bold mb-4">Portal Under Maintenance</h1>
            <p className="text-muted-foreground mb-6">
              We're currently performing scheduled maintenance to improve your experience. 
              Please check back shortly.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              For urgent support, contact us at{' '}
              <a href="mailto:support@shadowscan.com.au" className="text-primary hover:underline">
                support@shadowscan.com.au
              </a>
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checking || loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Portal Dashboard | ShadowScan</title>
          <meta
            name="description"
            content="View your protected WordPress domains, protection status, billing details, and monitoring activity in the ShadowScan portal with quick actions and support."
          />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  const containmentSummary = loginContainmentCount > 0
    ? 'Used recently'
    : 'None in last 30 days';

  const filteredDomains = domains?.filter(d => {
    const matchesSearch = d.domain_name.toLowerCase().includes(searchQuery.toLowerCase());
    const managedSite = managedSiteByDomainId.get(d.id);
    const disconnected = !isHeartbeatFresh(managedSite?.last_heartbeat_at);
    const matchesStatus = 
      statusFilter === 'all' ? true :
      statusFilter === 'pending' ? isPendingStatus(d.status) :
      statusFilter === 'active' ? d.status === 'active' :
      statusFilter === 'disconnected' ? disconnected :
      statusFilter === 'alerts' ? domainIdsWithAlerts.has(d.id) :
      true;
    return matchesSearch && matchesStatus;
  }) || [];

  const setupNeededDomains = (domains || []).filter((domain: any) => {
    if (domain.status === 'deleted') return false;
    const managedSite = managedSiteByDomainId.get(domain.id);
    return !managedSite?.last_heartbeat_at;
  });
  const setupActionableDomains = setupNeededDomains.filter((domain: any) => {
    const managedSite = managedSiteByDomainId.get(domain.id);
    return !managedSite?.last_heartbeat_at;
  });
  const setupProvisioningDomains = [] as any[];
  const setupActionableIds = setupActionableDomains.map((domain: any) => domain.id);
  const setupPrimaryDomain = setupActionableDomains[0] || null;
  const setupUrl = setupPrimaryDomain
    ? `/portal/add-domain?step=secure&domain_id=${setupPrimaryDomain.id}${
        setupActionableIds.length > 1 ? `&bulk=${setupActionableIds.join(',')}` : ''
      }`
    : '/portal/add-domain?step=secure';
  const configurationReadiness = setupActionableDomains.length > 0 || (domains && domains.length === 0)
    ? 'Action recommended'
    : 'Baseline applied';
  const billingIssueDomains = (domains || []).filter((domain: any) => isPaymentNeeded(domain.status));
  const repairActionableDomains = (domains || []).filter((domain: any) => {
    if (domain.status === 'deleted') return false;
    const managedSite = managedSiteByDomainId.get(domain.id);
    if (getConnectionState(managedSite?.last_heartbeat_at) === 'connected') return false;
    // Use effective protection state so test-mode/bypass views remain consistent.
    return getProtectionState(domain.status).rank > 0;
  });
  const repairPrimary = repairActionableDomains[0] || null;
  const showBillingBanner = billingIssueDomains.length > 0 && !paymentBypassUser;
  const accountBillingLine = accountBillingState === 'past_due'
    ? 'Billing past due - protection updates are paused. Resume billing to restore live updates and configuration changes.'
    : accountBillingState === 'suspended'
      ? 'Subscription suspended - this site is not receiving security updates. Reactivate to restore live updates.'
      : accountBillingState === 'paused'
        ? 'Protection is paused due to subscription status. Reactivate to restore protection.'
        : '';

  const hideCustomerUi = !allowAdminView && isAdmin === true && !loading && !checking;
  if (hideCustomerUi) {
    return null;
  }
  const impersonationHomePath = isImpersonationView && impersonationContext?.customer?.id
    ? `/portal/admin/impersonate/${impersonationContext.customer.id}`
    : '/portal';

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>Portal Dashboard | ShadowScan</title>
          <meta
            name="description"
            content="Manage your ShadowScan protection: review domains, protection status, billing summaries, and support options from your portal dashboard."
          />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <PortalTopNav
        userEmail={user.email || ''}
        userName={accountMenuName}
        isAdmin={!allowAdminView && isAdmin === true}
        context={impersonationExitTo ? 'admin_impersonation' : undefined}
        homePath={impersonationHomePath}
        onExitImpersonation={impersonationExitTo ? () => {
          clearImpersonation();
          navigate(impersonationExitTo);
        } : undefined}
        active="dashboard"
      />

      {/* Notices */}
      {showBillingBanner && (
        <div className="relative z-10 container mx-auto px-4 pt-4 max-w-6xl">
          <div className="flex flex-col gap-3 rounded-lg border border-[hsl(var(--ss-warning)/0.3)] bg-[hsl(var(--ss-warning)/0.12)] px-4 py-3 md:flex-row md:items-center">
            <AlertTriangle className="h-5 w-5 text-[hsl(var(--ss-warning))]" />
            <div className="text-sm text-foreground flex-1">
              <p className="font-semibold">Billing issue detected</p>
              <p className="text-muted-foreground">
                Protection continues, but updates and configuration changes are paused until billing resumes.
              </p>
            </div>
            <Button size="sm" onClick={handleManageBilling}>
              Fix billing
            </Button>
          </div>
        </div>
      )}
      {repairActionableDomains.length > 0 && (
        <div className="relative z-10 container mx-auto px-4 pt-4 max-w-6xl">
          <div className="flex flex-col gap-3 rounded-lg border border-[hsl(var(--ss-info)/0.3)] bg-[hsl(var(--ss-info)/0.12)] px-4 py-3 md:flex-row md:items-center">
            <AlertCircle className="h-5 w-5 text-[hsl(var(--ss-info))]" />
            <div className="text-sm text-foreground flex-1">
              {repairActionableDomains.every((d: any) => d.status === 'trial') ? (
                <>
                  <p className="font-semibold">Connection needs attention. Trial active.</p>
                  <p className="text-muted-foreground">
                    Check-ins are delayed or stopped. Run repair setup to restore reporting.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold">Connection needs attention. Subscription still active.</p>
                  <p className="text-muted-foreground">
                    Check-ins are delayed or stopped. Run repair setup to restore reporting, or manage billing in the portal.
                  </p>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {repairPrimary && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRepairConnection(repairPrimary.id)}
                >
                  Repair connection
                </Button>
              )}
              {!repairActionableDomains.every((d: any) => d.status === 'trial') && (
                <Button size="sm" onClick={handleManageBilling}>
                  Open billing
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {showSecurityNotice && (
        <div className="relative z-10 container mx-auto px-4 pt-4 max-w-6xl">
          <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3">
            <AlertCircle className="h-5 w-5 text-primary mt-0.5" />
            <div className="text-sm text-foreground">
              <p className="font-semibold">Security Alerts Upgrade</p>
              <p className="text-muted-foreground">
                We&apos;re improving how alerts reach your dashboard. Existing monitoring remains active.
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto -mr-2 text-muted-foreground hover:text-foreground"
              onClick={handleDismissSecurityNotice}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {bannerNotifications.length > 0 && (
        <div className="relative z-10 container mx-auto px-4 pt-4 max-w-6xl">
          <div className="flex flex-col gap-3 rounded-lg border border-[hsl(var(--ss-warning)/0.3)] bg-[hsl(var(--ss-warning)/0.12)] px-4 py-3 md:flex-row md:items-start">
            <Bell className="h-5 w-5 text-[hsl(var(--ss-warning))] mt-0.5" />
            <div className="text-sm text-foreground flex-1">
              <p className="font-semibold">
                You have {bannerNotifications.length} new notification{bannerNotifications.length === 1 ? '' : 's'}
              </p>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                {bannerNotifications.slice(0, 5).map((item: any) => (
                  <li key={item.id} className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        item.severity === 'success'
                          ? 'bg-[hsl(var(--ss-success))]'
                          : item.severity === 'warning'
                            ? 'bg-[hsl(var(--ss-warning))]'
                            : item.severity === 'error'
                              ? 'bg-[hsl(var(--ss-danger))]'
                              : 'bg-[hsl(var(--ss-neutral))]'
                      }`}
                    />
                    <span className="flex-1">{item.title}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="-mr-1 h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => dismissNotification(item.id)}
                      aria-label="Dismiss notification"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </li>
                ))}
                {bannerNotifications.length > 5 && (
                  <li className="text-xs text-muted-foreground">
                    +{bannerNotifications.length - 5} more
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {setupActionableDomains.length > 0 && (
        <div className="relative z-10 container mx-auto px-4 pt-4 max-w-6xl">
          <div className="flex flex-col gap-4 rounded-lg border border-[hsl(var(--ss-success)/0.3)] bg-[hsl(var(--ss-success)/0.12)] px-4 py-3 md:flex-row md:items-center">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--ss-success))]" />
            <div className="text-sm text-foreground flex-1">
              <p className="font-semibold">Continue secure setup</p>
              <p className="text-muted-foreground">
                {setupActionableDomains.length === 1 && setupPrimaryDomain
                  ? `Finish securing ${setupPrimaryDomain.domain_name}.`
                  : `${setupActionableDomains.length} sites still need security setup.`}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate(setupUrl)}
            >
              Continue Secure Setup
            </Button>
          </div>
        </div>
      )}

      {setupProvisioningDomains.length > 0 && showSetupNotice && (
        <div className="relative z-10 container mx-auto px-4 pt-4 max-w-6xl">
          <div className="flex flex-col gap-3 rounded-lg border border-[hsl(var(--ss-info)/0.3)] bg-[hsl(var(--ss-info)/0.12)] px-4 py-3 md:flex-row md:items-start">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--ss-info))] mt-0.5" />
            <div className="text-sm text-foreground flex-1">
              <p className="font-semibold">Security provisioning in progress</p>
              <p className="text-muted-foreground">
                {setupProvisioningDomains.length === 1 && setupProvisioningDomains[0]
                  ? `Connector installed for ${setupProvisioningDomains[0].domain_name}. We’re provisioning protection now.`
                  : `Connector installed for ${setupProvisioningDomains.length} sites. We’re provisioning protection now.`}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                This may take a few minutes. You can safely continue using the portal.
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto -mr-2 text-muted-foreground hover:text-foreground"
              onClick={handleDismissSetupNotice}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {portfolioPricingApplication && (
        <div className="relative z-10 container mx-auto px-4 pt-4 max-w-6xl">
          <PortfolioPricingStatusCard
            application={portfolioPricingApplication}
            compact
            activateDomainHref="/portal/add-domain"
            reviewedPricingSchedule={latestReviewedPricingSchedule}
          />
        </div>
      )}

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-mono font-bold mb-2">
            {isImpersonationView ? 'Customer dashboard' : 'Welcome back'}
          </h1>
          <p className="text-muted-foreground">
            {customerDisplayName}
          </p>
          {(isTestUser || paymentBypassUser) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {isTestUser && (
                <Badge variant={getStatusBadgeVariant('info')} className="text-xs">
                  Test mode{emulatedStatus ? ` · Emulated: ${emulatedStatus.toUpperCase()}` : ''}
                </Badge>
              )}
              {paymentBypassUser && (
                <Badge variant={getStatusBadgeVariant('warning')} className="text-xs">
                  Payment bypass enabled
                </Badge>
              )}
            </div>
          )}
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-mono font-bold mb-3">Protection overview</h2>
          {accountBillingState !== 'active' && accountBillingLine && (
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-[hsl(var(--ss-warning)/0.3)] bg-[hsl(var(--ss-warning)/0.12)] px-3 py-2 text-sm text-foreground">
              <span className="flex-1 text-muted-foreground">{accountBillingLine}</span>
              <Button size="sm" variant="outline" onClick={handleManageBilling}>
                {accountBillingState === 'past_due' ? 'Resume billing' : 'Reactivate'}
              </Button>
            </div>
          )}
          <div className="rounded-lg border border-border/50 bg-background/60 p-4">
            <div className="grid gap-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2">
                <span className="flex items-center gap-2 text-muted-foreground">
                  Protection status
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground/80 hover:text-foreground"
                          aria-label="Protection status help"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="space-y-2 text-xs">
                          <p className="font-semibold">
                            {protectionTooltipCopy[accountProtectionState.label]?.title || 'Protection status'}
                          </p>
                          <p className="text-muted-foreground">
                            {protectionTooltipCopy[accountProtectionState.label]?.body || 'Protection status reflects update and monitoring availability.'}
                          </p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
                <Badge variant={getStatusBadgeVariant(accountProtectionState.tone)} className="text-xs">
                  {accountProtectionState.label}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2">
                <span className="text-muted-foreground">Last successful check-in</span>
                <span className="font-medium text-foreground">{accountLastCheckIn}</span>
              </div>
              <div className="flex flex-col gap-1 border-b border-border/40 pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-muted-foreground">Issues requiring action</span>
                  <span className={`font-medium ${actionableIssueCount > 0 ? 'text-[hsl(var(--ss-danger))]' : 'text-foreground'}`}>
                    {actionableIssueCount}
                  </span>
                </div>
                {actionableIssueCount === 0 ? (
                  <span className="text-xs text-muted-foreground">No active issues detected.</span>
                ) : (
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline self-start"
                    onClick={() => {
                      setSelectedAlertDomain(null);
                      setAlertDialogOpen(true);
                    }}
                  >
                    View issue details
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2">
                <span className="text-muted-foreground">Containment activity</span>
                <span className={`font-medium ${loginContainmentCount > 0 ? 'text-[hsl(var(--ss-warning))]' : 'text-foreground'}`}>
                  {containmentSummary}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-muted-foreground">Configuration readiness</span>
                <Badge
                  variant={getStatusBadgeVariant(
                    configurationReadiness === 'Baseline applied' ? 'success' : 'warning',
                  )}
                  className="text-xs"
                >
                  {configurationReadiness}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Active Filter Indicator */}
        {statusFilter !== 'all' && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-muted-foreground">Filtering by:</span>
            <Badge
              variant={getStatusBadgeVariant(
                statusFilter === 'active' ? 'success' : statusFilter === 'disconnected' ? 'warning' : 'danger',
              )}
              className="text-xs"
            >
              {statusFilter === 'active'
                ? 'Reporting'
                : statusFilter === 'disconnected'
                  ? 'Disconnected'
                  : 'Issues requiring action'}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStatusFilter('all')}
              className="h-6 px-2 text-xs"
            >
              Clear
            </Button>
          </div>
        )}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-mono font-bold">
            Your Domains
          </h2>
          <Button onClick={handleAddDomain}>
            <Plus className="h-4 w-4 mr-2" />
            Add Domain
          </Button>
        </div>
        <>
          {/* Search and Filter Bar */}
          {domains && domains.length > 0 && (
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search domains..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-background/50 border-border/50"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'active' | 'alerts' | 'disconnected')}
                className="px-4 py-2 rounded-md border border-border/50 bg-background/50 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="active">Protected</option>
                <option value="disconnected">Disconnected</option>
                <option value="alerts">Issues requiring action</option>
              </select>
            </div>
          )}

          {domainsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading domains...</div>
          ) : domains && domains.length > 0 ? (
            filteredDomains.length > 0 ? (
              <div className="grid gap-4">
                {filteredDomains.map((domain) => {
                  const managedSite = managedSiteByDomainId.get(domain.id);
                  const connectionState = getConnectionState(managedSite?.last_heartbeat_at);
                  const heartbeatConnected = connectionState === 'connected';
                  const repairNeeded = isHeartbeatRepairNeeded(managedSite?.last_heartbeat_at);
                  const protectionState = getProtectionState(domain.status);
                  const repairActionable =
                    domain.status !== 'deleted' &&
                    connectionState !== 'connected' &&
                    protectionState.rank > 0;
                  const billingState = getBillingState(domain.status);
                  const healthSummary = domainHealthSummary.healthByDomainId.get(domain.id);
                  const healthState = getSecurityHealthLabel(healthSummary?.status || 'monitoring_planned');
                  const posture = postureByDomain.get(domain.id);
                  const issueCount = issuesByDomain.get(domain.id) || 0;
                  const detailsOpen = Boolean(expandedDomains[domain.id]);

                  return (
                    <Card key={domain.id} className="cyber-card border-border/50">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {domain.status === 'active' ? (
                              <ShieldCheck className="h-5 w-5 text-[hsl(var(--ss-success))]" />
                            ) : isPaymentNeeded(domain.status) ? (
                              <ShieldAlert className="h-5 w-5 text-[hsl(var(--ss-warning))]" />
                            ) : isPendingStatus(domain.status) ? (
                              <Shield className="h-5 w-5 text-[hsl(var(--ss-warning))]" />
                            ) : (
                              <Shield className="h-5 w-5 text-muted-foreground" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-primary" />
                                <a 
                                  href={`https://${domain.domain_name.replace(/^https?:\/\//, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-mono font-medium hover:text-primary transition-colors hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                    {domain.domain_name}
                                  </a>
                                </div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <Badge
                                    variant={getStatusBadgeVariant(
                                      connectionState === 'connected'
                                        ? 'success'
                                        : repairNeeded
                                          ? 'warning'
                                          : 'danger',
                                    )}
                                    className="text-xs"
                                  >
                                    Connection:{' '}
                                    {connectionState === 'connected'
                                      ? 'Connected'
                                      : repairNeeded
                                        ? 'Check-in delayed'
                                        : 'Disconnected'}
                                  </Badge>
                                  <Badge
                                    variant={getStatusBadgeVariant(billingState.tone)}
                                    className="text-xs"
                                  >
                                    Billing: {billingState.label}
                                  </Badge>
                                  <Badge
                                    variant={getStatusBadgeVariant(protectionState.tone)}
                                    className="text-xs"
                                  >
                                    Protection: {protectionState.label}
                                  </Badge>
                                  <Badge
                                    variant={getStatusBadgeVariant(healthState.tone)}
                                    className="text-xs"
                                  >
                                    Security Health: {healthState.label}
                                  </Badge>
                                  {posture ? (
                                    <>
                                      <Badge variant="info" className="text-xs">
                                        Coverage: {posture.coverage_score}/100
                                      </Badge>
                                      <Badge variant="info" className="text-xs">
                                        Posture: {posture.posture_score}/100
                                      </Badge>
                                    </>
                                  ) : null}
                                  {domain.stripe_price_id && TIER_BY_PRICE_ID[domain.stripe_price_id] ? (
                                    <Badge variant={TIER_BY_PRICE_ID[domain.stripe_price_id].variant} className="text-xs">
                                      {getDomainLabel(domain)}
                                    </Badge>
                                  ) : domain.tier ? (
                                    <Badge variant={getTierType(domain) === 'premium' ? 'default' : 'secondary'} className="text-xs">
                                      {getDomainLabel(domain)}
                                    </Badge>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <p className="text-xs text-muted-foreground/80 hidden sm:block">
                                Added {new Date(domain.created_at).toLocaleDateString()}
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleDomainDetails(domain.id)}
                              >
                                {detailsOpen ? 'Hide details' : 'Details'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/portal/domains/${domain.id}`)}
                              >
                                Open
                              </Button>
                              {repairActionable && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRepairConnection(domain.id)}
                                >
                                  Repair
                                </Button>
                              )}
                              {isPaymentNeeded(domain.status) && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={resumingDomainId === domain.id}
                                    onClick={() => handleResumePayment(domain)}
                                  >
                                    {resumingDomainId === domain.id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Opening...
                                      </>
                                    ) : (
                                      <>
                                        <span className="hidden sm:inline">{isImpersonationView ? 'Resend Payment Email' : 'Resume Payment'}</span>
                                        <span className="sm:hidden">$</span>
                                      </>
                                    )}
                                  </Button>
                                </>
                              )}
                              {domain.tier === 'basic' && !domain.security_addon_tier && !isPaymentNeeded(domain.status) && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate(`/portal/add-domain?base=hosting_basic&addon=essential&domain_id=${domain.id}`)}
                                  >
                                    Add Essential
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate(`/portal/add-domain?base=hosting_basic&addon=premium&domain_id=${domain.id}`)}
                                  >
                                    Add Premium
                                  </Button>
                                </>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    disabled={deletingDomainId === domain.id}
                                  >
                                    {deletingDomainId === domain.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove site</AlertDialogTitle>
                                  <AlertDialogDescription>
                                      Remove <strong>{domain.domain_name}</strong>? This stops monitoring and billing for this site.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                  <div className="py-2 space-y-3">
                                    <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                                      <div>
                                        Plan:{' '}
                                        <span className="text-foreground">
                                          {getDomainLabel(domain)}
                                        </span>
                                      </div>
                                      <div>
                                        {isImpersonationView ? 'Removal flow:' : 'Cancellation fee:'}{' '}
                                        {isImpersonationView ? (
                                          <span className="text-foreground">
                                            Customer confirmation email (no cancellation fee) by default
                                          </span>
                                        ) : (
                                          <span className="text-foreground">
                                            {domain.tier === 'dev'
                                              ? 'No cancellation fee under reviewed pricing terms'
                                              : formatCurrency(isPaymentNeeded(domain.status) ? 0 : CANCELLATION_FEES[getTierType(domain)])}
                                          </span>
                                        )}
                                      </div>
                                      {isImpersonationView ? (
                                        <div>Force remove is logged and requires a reason.</div>
                                      ) : domain.tier === 'dev' ? (
                                        <div>No partial-month refund applies. Future billing stops from the next billing cycle after removal.</div>
                                      ) : (
                                        <div>Reason: covers initial setup and configuration. No partial-month refund applies after removal unless required by law.</div>
                                      )}
                                    </div>
                                    <Label htmlFor={`confirm-${domain.id}`} className="text-sm text-muted-foreground">
                                      Type the domain name to confirm:
                                    </Label>
                                    <Input
                                      id={`confirm-${domain.id}`}
                                      value={confirmDomainInput}
                                      onChange={(e) => setConfirmDomainInput(e.target.value)}
                                      placeholder={domain.domain_name}
                                      className="mt-2"
                                    />
                                    {isImpersonationView ? (
                                      <>
                                        <label className="flex items-start gap-2 text-xs text-muted-foreground">
                                          <input
                                            type="checkbox"
                                            checked={Boolean(impersonationForceRemove[domain.id])}
                                            onChange={(e) => {
                                              const checked = e.target.checked;
                                              setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: checked }));
                                              if (!checked) {
                                                setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
                                              }
                                            }}
                                          />
                                          <span>Force remove immediately (skip customer confirmation email).</span>
                                        </label>
                                        {Boolean(impersonationForceRemove[domain.id]) && (
                                          <div>
                                            <Label htmlFor={`force-reason-${domain.id}`} className="text-sm text-muted-foreground">
                                              Force remove reason (required)
                                            </Label>
                                            <Input
                                              id={`force-reason-${domain.id}`}
                                              value={impersonationForceReason[domain.id] || ''}
                                              onChange={(e) => setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: e.target.value }))}
                                              placeholder="Provide the operational reason for immediate removal"
                                              className="mt-2"
                                            />
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <label className="flex items-start gap-2 text-xs text-muted-foreground">
                                        <input
                                          type="checkbox"
                                          checked={Boolean(confirmCancelFees[domain.id])}
                                          onChange={(e) => setConfirmCancelFees((prev) => ({ ...prev, [domain.id]: e.target.checked }))}
                                        />
                                        <span>
                                          {domain.tier === 'dev'
                                            ? 'I understand no cancellation fee applies under the reviewed pricing terms and that no partial-month refund is issued for the current billing period.'
                                            : 'I understand the cancellation fee will be charged to my payment method and that no partial-month refund applies after removal unless required by law.'}
                                        </span>
                                      </label>
                                    )}
                                  </div>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel
                                      onClick={() => {
                                        setConfirmDomainInput('');
                                        setConfirmCancelFees((prev) => ({ ...prev, [domain.id]: false }));
                                        setImpersonationForceRemove((prev) => ({ ...prev, [domain.id]: false }));
                                        setImpersonationForceReason((prev) => ({ ...prev, [domain.id]: '' }));
                                      }}
                                    >
                                      Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleDeleteDomain(domain)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      disabled={
                                        isImpersonationView
                                          ? Boolean(impersonationForceRemove[domain.id]) && !(impersonationForceReason[domain.id] || '').trim()
                                          : !confirmCancelFees[domain.id]
                                      }
                                    >
                                      {isImpersonationView
                                        ? (impersonationForceRemove[domain.id] ? 'Force remove site' : 'Send confirmation email')
                                        : 'Remove site'}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>

                          {detailsOpen && (
                            <div className="mt-4 border-t border-border/50 pt-4">
                              <div className="mb-3 grid grid-cols-1 gap-2 text-xs text-muted-foreground sm:grid-cols-2 lg:grid-cols-4">
                                <div className="flex items-center justify-between rounded-md border border-border/40 bg-background/40 px-3 py-2">
                                  <span>Last successful check-in</span>
                                  <span>{managedSite?.last_heartbeat_at ? formatTimestamp(managedSite.last_heartbeat_at) : 'Not yet'}</span>
                                </div>
                                <div className="flex items-center justify-between rounded-md border border-border/40 bg-background/40 px-3 py-2">
                                  <span>Security Health</span>
                                  <span className={healthState.tone === 'danger' ? 'text-[hsl(var(--ss-danger))]' : healthState.tone === 'warning' ? 'text-[hsl(var(--ss-warning))]' : 'text-foreground'}>
                                    {healthState.label}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between rounded-md border border-border/40 bg-background/40 px-3 py-2">
                                  <span>Last coverage check</span>
                                  <span>{healthSummary?.lastChecked ? formatTimestamp(healthSummary.lastChecked) : 'Not yet'}</span>
                                </div>
                                <div className="flex items-center justify-between rounded-md border border-border/40 bg-background/40 px-3 py-2">
                                  <span>Issues requiring action</span>
                                  {issueCount > 0 ? (
                                    <button
                                      className="text-xs text-[hsl(var(--ss-warning))] underline-offset-2 hover:underline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedAlertDomain(domain);
                                        setAlertDialogOpen(true);
                                      }}
                                    >
                                      {issueCount} open
                                    </button>
                                  ) : (
                                    <span className="text-[hsl(var(--ss-success))]">None detected</span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between rounded-md border border-border/40 bg-background/40 px-3 py-2">
                                  <span>Containment activity</span>
                                  <span className={loginContainmentByDomain.get(domain.id) ? 'text-[hsl(var(--ss-warning))]' : 'text-foreground'}>
                                    {loginContainmentByDomain.get(domain.id) ? 'Used recently' : 'None in last 30 days'}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between rounded-md border border-border/40 bg-background/40 px-3 py-2">
                                  <span>Connector status</span>
                                  <span className={heartbeatConnected ? 'text-[hsl(var(--ss-success))]' : 'text-[hsl(var(--ss-warning))]'}>
                                    {heartbeatConnected ? 'Connected' : 'Not reporting'}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-col gap-3 rounded-lg border border-border/50 bg-muted/20 p-3 text-sm">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className="font-semibold text-foreground">Connector details</span>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    {managedSite?.last_heartbeat_at && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleSelfCheck(managedSite)}
                                      >
                                        Refresh status
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                <div className="space-y-1 text-xs text-muted-foreground">
                                  <p>
                                    {connectionState === 'connected'
                                      ? 'ShadowScan is receiving telemetry from this site.'
                                      : repairNeeded
                                        ? 'Last check-in delayed. Run repair setup to restore normal reporting.'
                                        : managedSite?.last_heartbeat_at
                                          ? 'Check-ins have stopped. Run repair setup to restore reporting.'
                                          : 'Connector plugin not detected yet. Install and connect to continue setup.'}
                                  </p>
                                </div>
                                {repairActionable && (
                                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-[hsl(var(--ss-warning)/0.3)] bg-[hsl(var(--ss-warning)/0.1)] p-2 text-xs">
                                    <span className="text-muted-foreground">
                                      {repairNeeded
                                        ? 'Last check-in delayed.'
                                        : 'Connection lost.'}
                                    </span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRepairConnection(domain.id)}
                                    >
                                      Repair connection
                                    </Button>
                                  </div>
                                )}
                                {!managedSite?.last_heartbeat_at && (
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleRepairConnection(domain.id)}
                                    >
                                      Continue setup
                                    </Button>
                                  </div>
                                )}
                                {managedSite?.last_heartbeat_at && <div />}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No domains match "{searchQuery}"
                </div>
              )
            ) : (
              <Card className="cyber-card">
                <CardContent className="p-8 text-center">
                  <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <CardTitle className="text-lg mb-2">No domains yet</CardTitle>
                  <CardDescription className="mb-4">
                    Add your first WordPress site to start ShadowScan protection and service visibility
                  </CardDescription>
                  <Button onClick={handleAddDomain}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Domain
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
      </main>

      {/* Alerts Modal */}
      <AlertDialog
        open={alertDialogOpen}
        onOpenChange={(open) => {
          setAlertDialogOpen(open);
          if (!open) setSelectedAlertDomain(null);
        }}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Security issues {selectedAlertDomain ? `- ${selectedAlertDomain.domain_name}` : '- All sites'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {selectedDomainAlerts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No active issues detected.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                    {selectedDomainAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className="p-3 border border-border/50 rounded-lg bg-muted/30"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs text-muted-foreground font-mono uppercase tracking-tight">
                              {alert.alert_type || 'Alert'}
                            </p>
                            <p className="font-semibold text-foreground">
                              {alert.title || 'Wordfence alert'}
                            </p>
                          </div>
                          <Badge
                            variant={
                              alert.severity === 'high'
                                ? 'danger'
                                : alert.severity === 'medium'
                                  ? 'warning'
                                  : 'neutral'
                            }
                            className="text-xs"
                          >
                            {alert.severity || 'medium'}
                          </Badge>
                        </div>
                        {alert.description && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {alert.description}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(alert.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CurrencyDock />
    </div>
  );
}
